'use client';

import {
  PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useEvent } from 'react-use';
import type { Scene } from '~/app/_types/chat-script';
import { resizeServingUrl } from '~/utils/image';
import { Box } from '@styled-system/jsx';

type Props = PropsWithChildren<{
  scene: Scene;
  sceneIndex: number;
  sceneCount: number;
}>;

export const ChatSceneBackgroundContainer = ({
  scene,
  sceneIndex,
  sceneCount,
  children,
}: Props) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  const backgroundImageUrl = useMemo(() => {
    if (!scene.background.imageUrl) return;

    return resizeServingUrl(scene.background.imageUrl, 1200) ?? undefined;
  }, [scene.background.imageUrl]);

  // Reach to scene bottom
  const [isReachbottom, setIsReachbottom] = useState(false);

  const isLastScene = useMemo(() => {
    return sceneIndex === sceneCount - 1;
  }, [sceneCount, sceneIndex]);

  const handleOnScroll = useCallback(() => {
    const bottomEelement = bottomRef.current;
    if (!isLastScene || !bottomEelement) return;
    const bottomTop = bottomEelement.getBoundingClientRect().top;
    // reach bottom
    setIsReachbottom(bottomTop < 0);
  }, [isLastScene]);

  useEvent('scroll', handleOnScroll);

  return (
    <Box
      w="100%"
      position="relative"
      style={{
        backgroundColor: scene.background.color ?? undefined,
      }}
    >
      {/* Display using two images only when the device is wide. */}
      {backgroundImageUrl && (
        <>
          {/* Default */}
          <Box position="sticky" w="100%" h="0" top="0">
            <Box
              visibility={!isLastScene || !isReachbottom ? 'visible' : 'hidden'}
              bgRepeat="no-repeat"
              bgSize="cover"
              backgroundPosition="center"
              w="100%"
              h="100lvh"
              position="absolute"
              top="0"
              left="0"
              right="0"
              style={{
                backgroundImage: `url(${backgroundImageUrl})`,
              }}
            />
          </Box>

          {/* Reach Bottom */}
          {isLastScene && (
            <Box position="absolute" w="100%" h="0" bottom="0">
              <Box
                visibility={!isReachbottom ? 'hidden' : 'visible'}
                ref={bottomRef}
                bgRepeat="no-repeat"
                bgSize="cover"
                backgroundPosition="center"
                w="100%"
                h="100lvh"
                position="absolute"
                bottom="0"
                left="0"
                right="0"
                style={{
                  backgroundImage: `url(${backgroundImageUrl})`,
                }}
              />
            </Box>
          )}
        </>
      )}
      <Box w="100%" position="relative" zIndex={1}>
        {children}
      </Box>
    </Box>
  );
};
